import {useCallback, useContext, useEffect, useState} from 'react';
import {Alert, Button, Col, Form, Input, notification, Row, Select, Space} from 'antd';
import {EditOutlined, SearchOutlined} from '@ant-design/icons';
import {IJobDemandReq, ILocation, JobTypeOption} from "../../services/types.ts";
import {
    addJobDemand,
    getHomeofficeOptions,
    getJobTypes,
    getStripeUrl,
    getTargetGroupOptions
} from "../../services/api.ts";
import {ENGAGEMENT_FORM_OPTIONS, WORK_LOAD_HOURS_OPTIONS} from "../../util/jobFormOptions.ts";
import {useQuery, UseQueryResult} from "@tanstack/react-query";
import {AuthContext} from "../../context/AuthContext.tsx";
import {AddressModal} from "../../components/AddressModal.tsx";
import {useLocation, useNavigate} from "react-router-dom";
import {PageTitle} from "candidate/src/components/PageTitle.tsx";
import {useMediaQuery} from "@uidotdev/usehooks";
import styled from "styled-components";
import {StyledInput} from "candidate/src/pages/jobPool/JobPool.tsx";
import {getToken} from "../../util/tokenCookies.ts";
import {FAQ} from "./FAQ.tsx";
import {JobSteps} from "../../components/Steps.tsx";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';


interface IFormValues {
    title: string;
    description: string;
    job_type: number;
    homeoffice: number;
    engagement_form?: string | null;
    work_load_min_hours?: number | null;
    work_load_max_hours?: number | null;
    targetGroup: number;
    contactEmail?: string | null;
}

const initialValues = {
    description: `
        <p><strong>[Tipp: Beschreiben Sie kurz und prägnant worum es geht]</strong></p>
        <p><em>Beispiel:</em> Wir suchen einen neuen Kollegen für unser XYZ Team, der uns bei der Bearbeitung von telefonischen Kundenanfragen und Ablage unterstützt.</p>
        <br/>
        <p><strong>Wo?</strong></p>
        <p><strong>[Tipp: Nennen Sie die konkrete Anschrift und ggf. auch öffentl. Verkehrsanbindung, wenn es mehrere Standorte gibt, nennen Sie alle]</strong></p>
        <ul>
            <li><em>Beispiel:</em> Grolmanstraße 48, 10623 Berlin, nähe S-Bahn Savignyplatz</li>
            <li><em>Beispiel:</em> An folgenden Standorten suchen wir: Charlottenburg, Wedding und Köpenick</li>
        </ul>
        <br/>
        <p><strong>Was wir bieten?</strong></p>
        <p><strong>[Tipp: Verkaufen Sie sich gut, Silvertalents sind stark nachgefragt]</strong></p>
        <ul>
            <li><em>Beispiel:</em> Obstkorb und Kaffee im Büro für alle</li>
            <li><em>Beispiel:</em> nettes Arbeitsumfeld</li>
        </ul>
        <br/>
        <p><strong>Was wir suchen?</strong></p>
        <p><strong>[Tipp: Wenn es wichtige Anforderungen gibt listen Sie sie auf, aber denken Sie daran, dass jede Anforderung den Bewerberkreis verkleinert]</strong></p>
        <ul>
            <li><em>Beispiel:</em> motivierte neue Kollegen</li>
            <li><em>Beispiel:</em> Kenntnisse in XYZ</li>
        </ul>
    `
};

export const NewJobDemand = () => {
    const [courseDetails, setCourseDetails] = useState<string>("");
    const {user} = useContext(AuthContext);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isWhereModalOpen, setIsWhereModalOpen] = useState(false);
    const [whereSelectedValue, setWhereSelectedValue] = useState<ILocation | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const isPrivateUser = user?._client?.isPrivate;
    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const isFromProfile = searchParams.get('from') === 'profile';
        if (!isFromProfile) {
            if (!user?._client?.profile_name && !user?._client?.isPrivate) {
                navigate("/new/profile");
            }
        }

    }, [location.search, navigate, user?._client]);

    const {data: jobTypes}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['jobTypes'],
        queryFn: async () => {
            const types = await getJobTypes();
            return types.map(type => ({...type, value: type.id}));
        }
    });

    const {data: homeofficeOptions}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['homeofficeOptions'],
        queryFn: async () => {
            const types = await getHomeofficeOptions();
            return types.map(type => ({...type, value: type.id}));
        }
    });

    const {data: targetGroupOptions}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['targetGroupOptions'],
        queryFn: async () => {
            const types = await getTargetGroupOptions();
            return types.map(type => ({...type, value: type.id}));
        }
    });

    const onFinish = async (values: IFormValues) => {
        setIsLoading(true)
        if (jobTypes && homeofficeOptions && targetGroupOptions && user) {
            const jobDemand: IJobDemandReq = {
                type: "job_ad",
                title: values.title,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                description: values.description,
                job_type_id: values.job_type,
                work_load_id: 0,
                job_type_label: jobTypes.find(type => type.id === values.job_type)?.label || '',
                job_demand_status_id: 3,
                client_id: user.client_id,
                users_id: user.id,
                search_location_label: whereSelectedValue!.label,
                search_location_id: whereSelectedValue!.id,
                regions_id: whereSelectedValue!.type === 'region_id' ? whereSelectedValue!.value || 0 : 0,
                postal_code: whereSelectedValue!.type !== 'region_id' ? whereSelectedValue!.value?.toString() || '' : '',
                radius: whereSelectedValue!.radius || 0,
                contactEmail: values?.contactEmail || null,
                engagement_form: values.engagement_form || null,
                work_load_min_hours: values.work_load_min_hours ?? null,
                work_load_max_hours: values.work_load_max_hours ?? null,
            }
            try {
                await addJobDemand(jobDemand);
                window.scrollTo(0, 0);
                if (!isPrivateUser) {
                    navigate(user.plan_type === "paid" ? `/jobs` : "/finances?from=newJobDemand")
                    return
                }
                const authToken = getToken();

                const stripeResponse = await getStripeUrl({
                    id: authToken,
                    product: "Private",
                    redirectPath: `jobs`
                });
                if (stripeResponse.url) {
                    window.location.href = stripeResponse.url;
                } else {
                    notification.error({message: "Es ist ein Fehler beim Anlegen der Stellenanzeige aufgetreten."});
                }
                setIsLoading(false);
            } catch (e) {
                console.log(e)
                setIsLoading(false);
            }
        }
    };

    const onWhereLooking = () => {
        setIsWhereModalOpen(true);
    };

    const handleSaveWhere = useCallback((value: ILocation | null) => {
        if (value) {
            form.setFieldsValue({location: value.label_client});
            setWhereSelectedValue(value);
        }
    }, [form]);

    return (
        <div>
            <PageTitle title="Neue Stellenanzeige anlegen"/>
            <Row>
                {user?.plan_type !== "paid" && !isPrivateUser && <JobSteps activeStep="anzeige"/>}

                <Col span={isMobile ? 24 : 12}>
                    <Space direction="vertical" size="large" style={{width: '100%'}}>
                        <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onFinish}>
                            <Form.Item
                                name="title"
                                label="Titel der Stelle:"
                                rules={[{required: true, message: 'Bitte füllen Sie alle Felder aus.'}]}
                            >
                                <Input size={isMobile ? "large" : "middle"} placeholder="Titel der Stelle"/>
                            </Form.Item>

                            <Form.Item
                                name="location"
                                label="Standort:"
                                rules={[{required: true, message: 'Bitte füllen Sie alle Felder aus.'}]}
                            >
                                <StyledInput
                                    onClick={onWhereLooking}
                                    readOnly
                                    style={{width: '100%'}}
                                    prefix={isMobile ? <SearchOutlined/> : <EditOutlined/>}
                                    placeholder="Postleitzahl (empfohlen) oder Region wählen"
                                />
                            </Form.Item>

                            <Alert style={{marginBottom: 16}}
                                   message={(!whereSelectedValue || whereSelectedValue?.label_client.includes("Region:")) ?
                                       "Wählen Sie soweit möglich immer eine Postleitzahl und legen Sie für jeden Standort eine eigene Stellenanzeige an" :
                                       "Legen Sie für jeden Standort eine eigene Stellenanzeige an um alle Kandidaten zu erreichen"

                                   }
                                   showIcon type="info"/>
                            {isPrivateUser ? (<>
                                    <Form.Item name="engagement_form" label="Einsatzform:">
                                        <Select size={isMobile ? "large" : "middle"} placeholder="Einsatzform auswählen"
                                                options={ENGAGEMENT_FORM_OPTIONS} allowClear/>
                                    </Form.Item>
                                    <Form.Item label="Arbeitspensum:">
                                        <Space.Compact style={{width: '100%'}}>
                                            <Form.Item name="work_load_min_hours" noStyle>
                                                <Select size={isMobile ? "large" : "middle"} placeholder="von (min. Stunden)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 120}} allowClear/>
                                            </Form.Item>
                                            <Form.Item name="work_load_max_hours" noStyle rules={[({getFieldValue}) => ({validator(_, v) { const min = getFieldValue('work_load_min_hours'); if (v != null && min != null && v < min) return Promise.reject(new Error('Max. ≥ Min.')); return Promise.resolve();}})]}>
                                                <Select size={isMobile ? "large" : "middle"} placeholder="bis (max. Stunden)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 120}} allowClear/>
                                            </Form.Item>
                                        </Space.Compact>
                                        <span style={{fontSize: 12, color: '#666', marginTop: 4}}>pro Woche</span>
                                    </Form.Item>
                                    <Form.Item name="contactEmail" label="Bewerbungen senden an:" initialValue={user?.email}
                                               rules={[{
                                                   required: true,
                                                   type: "email",
                                                   message: 'Bitte geben Sie eine gültige E-Mail-Adresse an'
                                               }]}
                                    >
                                        <Input size={isMobile ? "large" : "middle"}
                                               placeholder="Email-Adresse"/>
                                    </Form.Item>

                                </>)
                                : <>
                                    <Form.Item name="job_type" label="Beschäftigungsart:">
                                        <Select size={isMobile ? "large" : "middle"} placeholder="Beschäftigungsart auswählen"
                                                options={jobTypes}/>
                                    </Form.Item>

                                    <Form.Item name="engagement_form" label="Einsatzform:">
                                        <Select size={isMobile ? "large" : "middle"} placeholder="Einsatzform auswählen"
                                                options={ENGAGEMENT_FORM_OPTIONS} allowClear/>
                                    </Form.Item>

                                    <Form.Item label="Arbeitspensum pro Woche:">
                                        <Space.Compact style={{width: '100%'}}>
                                            <Form.Item name="work_load_min_hours" noStyle>
                                                <Select size={isMobile ? "large" : "middle"} placeholder="von (min. Stunden)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 120}} allowClear/>
                                            </Form.Item>
                                            <Form.Item name="work_load_max_hours" noStyle rules={[({getFieldValue}) => ({validator(_, v) { const min = getFieldValue('work_load_min_hours'); if (v != null && min != null && v < min) return Promise.reject(new Error('Max. ≥ Min.')); return Promise.resolve();}})]}>
                                                <Select size={isMobile ? "large" : "middle"} placeholder="bis (max. Stunden)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 120}} allowClear/>
                                            </Form.Item>
                                        </Space.Compact>
                                        <span style={{fontSize: 12, color: '#666', marginTop: 4}}>pro Woche</span>
                                    </Form.Item>
                                </>}

                            <Form.Item name="description" label="Stellenbeschreibung (bitte anpassen):">
                                <ReactQuill theme="snow" placeholder="[Tipp: Beschreiben Sie kurz und prägnant worum es geht] Beispiel: Wir suchen einen neuen Kolleg" value={courseDetails} onChange={setCourseDetails}/>
                            </Form.Item>

                            <Alert style={{marginBottom: 16}}
                                   message="Ihr Inserat verlängert sich automatisch alle 30 Tage, bis Sie Ihr Inserat deaktivieren oder löschen"
                                   showIcon type="info"/>

                            <Form.Item>
                                <BtnWrapper>
                                    <Button onClick={() => navigate('/jobs')}>Zurück</Button>
                                    <Button type="primary" htmlType="submit" disabled={isLoading}>{user?.plan_type !== "paid" && !isPrivateUser ? "Weiter" : "Erstellen"}</Button>
                                </BtnWrapper>
                            </Form.Item>
                        </Form>
                    </Space>
                </Col>
                <Col span={isMobile ? 24 : 12} style={{paddingLeft: isMobile ? 0 : 32}}>
                    <FAQ isPrivateUser={!!isPrivateUser}/>
                </Col>
            </Row>
            <AddressModal
                open={isWhereModalOpen}
                onClose={() => setIsWhereModalOpen(false)}
                onSave={handleSaveWhere}
            />
        </div>
    );
};

const BtnWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`
